import {
  ApolloCache,
  ApolloError,
  Reference,
  useApolloClient,
} from '@apollo/client';
import {
  Category,
  CategoryMoveMutation,
  CategoryMoveQDocument,
  CategoryMoveQQuery,
  CategoryMoveQQueryVariables,
  OptimisticCategoryDocument,
  OptimisticCategoryQuery,
  OptimisticCategoryQueryVariables,
  OptimisticCollectionDocument,
  OptimisticCollectionQuery,
  OptimisticCollectionQueryVariables,
  useCategoryMoveMutation,
} from 'lib-knowledge-management-common/graphql';
import {
  CategoryRef,
  ParentRef,
} from 'lib-knowledge-management-common/models/types';
import { insertIntoArray } from 'lib-knowledge-utils/array';
import { ampli } from 'lib-shared-tracking';

type MutationFunction = ReturnType<typeof useCategoryMoveMutation>[0];
type MutationReturnValue = ReturnType<MutationFunction>;

export type MoveCategoryFn = (data: {
  categoryId: string;
  newParentRef: ParentRef;
  position?: number;
}) => MutationReturnValue;

type UseCategoryMoveReturnValue = [
  MoveCategoryFn,
  ReturnType<typeof useCategoryMoveMutation>[1],
];

type UseCategoryMoveParams = {
  onCompleted?: (res: CategoryMoveMutation) => void;
  onError?: (err: ApolloError) => void;
};
function useCategoryMove(
  params: UseCategoryMoveParams = {},
): UseCategoryMoveReturnValue {
  const [moveMutation, mutationResult] = useCategoryMoveMutation({
    onCompleted: params?.onCompleted,
    onError: params?.onError,
  });
  const { cache } = useApolloClient();
  const move: MoveCategoryFn = ({ categoryId, newParentRef, position }) => {
    const result = cache.readQuery<
      CategoryMoveQQuery,
      CategoryMoveQQueryVariables
    >({
      query: CategoryMoveQDocument,
      variables: { id: categoryId },
    });

    // Have everything we need?
    const category = result?.category;
    if (!category) throw new Error('Missing category in useCategoryMove');

    const oldParent = category?.parent;
    if (!oldParent)
      throw new Error('Missing category.parent in useCategoryMove');

    const optimisticResponse = createOptimisticResponse({
      cache,
      newParentRef,
      category,
      position,
    });

    return moveMutation({
      variables: {
        entityId: categoryId,
        currentParentId: oldParent.id,
        newParentId: newParentRef.id,
        newPosition: position,
      },

      optimisticResponse,

      update(_cache) {
        // Remove from old parent
        _cache.modify<Category>({
          id: _cache.identify(oldParent),
          fields: {
            categories(existingCategoryRefs = [], { readField }) {
              return existingCategoryRefs.filter((ref) => {
                return readField('id', ref!) !== categoryId;
              }) as Reference[];
            },
          },
        });

        // Add to new parent
        _cache.modify<Category>({
          id: _cache.identify(newParentRef),
          fields: {
            categories(existingCategoryRefs = [], { readField, toReference }) {
              // Filter out the category from where we're moving it into, just so we don't get duplicates
              const filtered = existingCategoryRefs.filter(
                (ref) => readField('id', ref!) !== categoryId,
              ) as Reference[];

              // if no order, place at end
              if (typeof position === 'undefined') {
                return [...filtered, toReference(category)] as Reference[];
              }
              return insertIntoArray(
                filtered,
                position - 1,
                toReference(category) as Reference,
              );
            },
          },
        });
      },
    }).then((args) => {
      if (typeof position !== 'undefined') {
        ampli.knowledgeCategoryReordered({ categoryId });
      } else {
        ampli.knowledgeCategoryUpdated({ categoryId });
      }
      return args;
    });
  };

  return [move, mutationResult];
}

export default useCategoryMove;

function createOptimisticResponse({
  cache,
  newParentRef,
  category,
  position,
}: {
  category: CategoryRef;
  newParentRef: ParentRef;
  cache: ApolloCache<object>;
  position: number | undefined;
}): CategoryMoveMutation | undefined {
  // Either query parent Collection or Category
  const optimisticParent =
    newParentRef.__typename === 'Collection'
      ? cache.readQuery<
          OptimisticCollectionQuery,
          OptimisticCollectionQueryVariables
        >({
          query: OptimisticCollectionDocument,
          variables: {
            id: newParentRef.id,
          },
        })?.collection
      : cache.readQuery<
          OptimisticCategoryQuery,
          OptimisticCategoryQueryVariables
        >({
          query: OptimisticCategoryDocument,
          variables: {
            id: newParentRef.id,
          },
        })?.category;
  if (!optimisticParent) return undefined;

  const childrenWithoutCategory = optimisticParent.categories.filter(
    (c) => c.id !== category.id,
  );

  const categoryObj = {
    __typename: category.__typename,
    id: category.id,
    parent: {
      __typename: newParentRef.__typename,
      id: newParentRef.id,
    },
  };

  const newChildrenArray =
    typeof position === 'undefined'
      ? [...childrenWithoutCategory, categoryObj]
      : insertIntoArray(childrenWithoutCategory, position - 1, categoryObj);

  return {
    __typename: 'Mutations',
    contentMove: {
      __typename: newParentRef.__typename,
      id: newParentRef.id,
      categories: newChildrenArray,
    },
  };
}
